/** Builder section 1 — who the plan is for: the advertiser and the agency buying for them. */

import React, { useMemo } from 'react';
import { Button } from '../../../ui';
import type { AdvertiserRecord, MediaPlanAgency, MediaPlanCategory } from '../types';
import { DIRECT_AGENCY, OTHER_AGENCY, fieldId, type PlanDraft } from '../planDraft';
import { DIRECT_AGENCY_LABEL, agencyOptions } from '../vocab';
import { fieldStyle, gridStyle, hintStyle, labelStyle } from '../formStyles';
import AdvertiserCombobox from './AdvertiserCombobox';
import FormSection from './FormSection';
import { FieldError, fieldAria, type ErrorFor } from './fields';

interface Props {
  draft: PlanDraft;
  advertisers: AdvertiserRecord[];
  categories: MediaPlanCategory[];
  agencies: MediaPlanAgency[];
  onPickAdvertiser: (record: AdvertiserRecord) => void;
  onTypeAdvertiser: (name: string) => void;
  onAddAdvertiser: (name: string) => void;
  addingAdvertiser: boolean;
  matchedName?: string;
  onAgencyChange: (agencyKey: string) => void;
  onAgencyOtherChange: (name: string) => void;
  /** Managers can confirm a suggested category from here. */
  onConfirmCategory?: () => void;
  confirmingCategory?: boolean;
  errorFor: ErrorFor;
  disabled?: boolean;
}

const WhoSection: React.FC<Props> = ({
  draft,
  advertisers,
  categories,
  agencies,
  onPickAdvertiser,
  onTypeAdvertiser,
  onAddAdvertiser,
  addingAdvertiser,
  matchedName,
  onAgencyChange,
  onAgencyOtherChange,
  onConfirmCategory,
  confirmingCategory = false,
  errorFor,
  disabled = false,
}) => {
  const sortedAgencies = useMemo(() => agencyOptions(agencies), [agencies]);
  const picked = draft.advertiserKey ? advertisers.find((a) => a.key === draft.advertiserKey) : undefined;
  const agencyError = errorFor('agencyOther');

  return (
    <FormSection id="mp-section-who" title="Advertiser" first>
      <div style={gridStyle}>
        <AdvertiserCombobox
          inputId={fieldId('advertiser')}
          advertisers={advertisers}
          categories={categories}
          advertiserKey={draft.advertiserKey}
          advertiserName={draft.advertiserName}
          onPick={onPickAdvertiser}
          onType={onTypeAdvertiser}
          onAddNew={onAddAdvertiser}
          adding={addingAdvertiser}
          matchedName={matchedName}
          error={errorFor('advertiser')}
          disabled={disabled}
        />
        <div>
          <label htmlFor={fieldId('agency')} style={labelStyle}>
            Agency
          </label>
          <select id={fieldId('agency')} style={{ ...fieldStyle, cursor: 'pointer' }} value={draft.agencyKey || DIRECT_AGENCY} disabled={disabled} onChange={(e) => onAgencyChange(e.target.value)}>
            <option value={DIRECT_AGENCY}>{DIRECT_AGENCY_LABEL}</option>
            {sortedAgencies.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
                {a.holdingCompany ? ` (${a.holdingCompany})` : ''}
              </option>
            ))}
            <option value={OTHER_AGENCY}>Other agency…</option>
          </select>
          {draft.agencyKey === OTHER_AGENCY && (
            <>
              <label htmlFor={fieldId('agencyOther')} style={{ ...labelStyle, marginTop: 10 }}>
                Agency name
              </label>
              <input
                id={fieldId('agencyOther')}
                style={fieldStyle}
                value={draft.agencyOther}
                maxLength={160}
                placeholder="e.g. Spark Foundry"
                disabled={disabled}
                onChange={(e) => onAgencyOtherChange(e.target.value)}
                {...fieldAria('agencyOther', agencyError)}
              />
              <FieldError field="agencyOther" message={agencyError} />
            </>
          )}
          <div style={hintStyle}>An agency's own rate card applies when the agency is in the list.</div>
        </div>
      </div>
      {picked?.categoryStatus === 'suggested' && onConfirmCategory && (
        <div style={styles.categoryNote}>
          <span style={{ flex: 1 }}>
            The category for {picked.name} ({categories.find((c) => c.key === picked.categoryKey)?.label || picked.categoryKey}) is still a suggestion.
          </span>
          <Button size="sm" variant="subtle" onClick={onConfirmCategory} disabled={confirmingCategory || disabled}>
            {confirmingCategory ? 'Confirming…' : 'Confirm category'}
          </Button>
        </div>
      )}
    </FormSection>
  );
};

const styles: Record<string, React.CSSProperties> = {
  categoryNote: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--fg-3)' },
};

export default WhoSection;
